import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar, Footer } from "../../components/Layout";
import { Copy, Check, Share2, Volume2, Sparkles, ArrowRight, BookOpen, GraduationCap } from "lucide-react";
import { useSEO } from "../../hooks/useSEO";
import { convertAll } from "../../lib/kana";
import { useTTS } from "../../hooks/useTTS";
import { toast } from "sonner";

const EXAMPLES = [
  { romaji: "konnichiwa",  meaning: "Bonjour" },
  { romaji: "arigatou",    meaning: "Merci" },
  { romaji: "sumimasen",   meaning: "Excusez-moi" },
  { romaji: "ohayou",      meaning: "Bonjour (matin)" },
  { romaji: "ganbatte",    meaning: "Bon courage" },
  { romaji: "kawaii",      meaning: "Mignon" },
];

const ResultRow = ({ label, value, color, onCopy, onSpeak, mono = true, copied, isJp }) => (
  <div className="bg-white rounded-2xl border-2 border-[#1A2A40]/10 p-5 sm:p-6">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color }}>
        {label}
      </div>
      <div className="flex items-center gap-1.5">
        {isJp && (
          <button
            onClick={onSpeak}
            aria-label={`Écouter ${label}`}
            data-testid={`speak-${label.toLowerCase()}`}
            className="p-2 rounded-lg hover:bg-[#F3F6F1] text-[#1A2A40]/70 hover:text-[#E94A3F] transition-colors"
          >
            <Volume2 className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={onCopy}
          aria-label={`Copier ${label}`}
          data-testid={`copy-${label.toLowerCase()}`}
          className="p-2 rounded-lg hover:bg-[#F3F6F1] text-[#1A2A40]/70 hover:text-[#E94A3F] transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-[#8CA07A]" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
    <div
      className={`${mono ? "font-jp" : "font-mono"} text-2xl sm:text-3xl text-[#1A2A40] break-words min-h-[2.5rem] leading-tight`}
      data-testid={`output-${label.toLowerCase()}`}
    >
      {value || <span className="text-[#1A2A40]/30">—</span>}
    </div>
  </div>
);

const RomajiKanaTool = () => {
  const [params, setParams] = useSearchParams();
  const initial = params.get("text") || "";
  const [input, setInput] = useState(initial);
  const [copied, setCopied] = useState({});
  const { speak } = useTTS();

  useSEO({
    title: "Convertisseur Romaji ↔ Hiragana ↔ Katakana gratuit | Japonais Pas à Pas",
    description:
      "Convertisseur romaji en hiragana et katakana, gratuit et instantané, sans inscription. Tape un mot ou une phrase, copie le résultat, écoute la prononciation native.",
    canonical: "https://japonais-pas-a-pas.com/outils/romaji-kana",
    keywords:
      "convertisseur romaji hiragana, romaji katakana, converter japonais, écrire en hiragana, écrire en katakana, traduire en kana, romaji to kana",
  });

  // Sync ?text=... in URL when user types (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (input) next.set("text", input);
      else next.delete("text");
      setParams(next, { replace: true });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const { romaji, hiragana, katakana, detected } = useMemo(
    () => convertAll(input),
    [input]
  );

  const copyWithWatermark = async (label, value) => {
    if (!value) return;
    const payload =
      `${value}\n` +
      `, converti sur https://japonais-pas-a-pas.com/outils/romaji-kana${
        input ? "?text=" + encodeURIComponent(input) : ""
      }`;
    try {
      await navigator.clipboard.writeText(payload);
      setCopied((c) => ({ ...c, [label]: true }));
      toast.success(`${label} copié`);
      setTimeout(() => setCopied((c) => ({ ...c, [label]: false })), 1500);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const share = async () => {
    const url =
      `https://japonais-pas-a-pas.com/outils/romaji-kana` +
      (input ? `?text=${encodeURIComponent(input)}` : "");
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Convertisseur Romaji ↔ Kana",
          text: input ? `${input} → ${hiragana}` : "Convertisseur japonais gratuit",
          url,
        });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié 🔗");
    } catch {
      toast.error("Impossible de partager");
    }
  };

  const speakJp = (text) => {
    if (!text) return;
    speak(text);
  };

  const hasOutput = Boolean(hiragana || katakana);

  return (
    <div className="washi-bg min-h-screen" data-testid="tool-romaji-kana">
      <Navbar />

      <header className="max-w-4xl mx-auto px-6 sm:px-10 pt-12 pb-8">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1.5 rounded-full border border-[#1A2A40]/15 text-xs font-bold tracking-widest text-[#E94A3F] mb-5">
          <Sparkles className="w-3.5 h-3.5" /> OUTIL GRATUIT · SANS INSCRIPTION
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A2A40] leading-[1.05] mb-4">
          Convertisseur <span className="text-[#E94A3F]">Romaji ↔ Kana</span>
        </h1>
        <p className="text-lg text-[#4A5568] max-w-2xl mb-2">
          Tape un mot ou une phrase en romaji (lettres latines), et obtiens la version{" "}
          <strong>hiragana</strong> ひらがな et <strong>katakana</strong> カタカナ instantanément.
          Avec audio natif et lien partageable.
        </p>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 pb-16 space-y-6">
        {/* Input */}
        <div className="bg-white rounded-2xl border-2 border-[#1A2A40]/10 p-5 sm:p-6 shadow-brut-sm">
          <label
            htmlFor="kana-input"
            className="block text-xs font-bold uppercase tracking-[0.2em] text-[#1A2A40]/70 mb-3"
          >
            Texte à convertir
          </label>
          <input
            id="kana-input"
            data-testid="kana-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="ex. konnichiwa, arigatou, watashi wa..."
            className="w-full text-2xl sm:text-3xl font-mono bg-transparent border-0 outline-none text-[#1A2A40] placeholder:text-[#1A2A40]/25"
            autoFocus
          />
          <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t border-[#1A2A40]/10">
            <div className="text-xs text-[#4A5568]">
              {input
                ? <>Détecté : <strong className="text-[#1A2A40]">{detected}</strong></>
                : <>Tape n'importe quoi en romaji, hiragana ou katakana</>}
            </div>
            <div className="flex gap-2">
              {input && (
                <button
                  onClick={() => setInput("")}
                  data-testid="kana-clear"
                  className="text-xs font-bold uppercase tracking-wider text-[#4A5568] hover:text-[#E94A3F] transition-colors px-3 py-1.5"
                >
                  Effacer
                </button>
              )}
              <button
                onClick={share}
                data-testid="kana-share"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#1A2A40] hover:text-[#E94A3F] transition-colors px-3 py-1.5"
              >
                <Share2 className="w-3.5 h-3.5" /> Partager
              </button>
            </div>
          </div>
        </div>

        {/* Quick examples */}
        {!input && (
          <div className="flex flex-wrap gap-2" data-testid="kana-examples">
            <span className="text-xs text-[#4A5568] mr-1 self-center">Essaie :</span>
            {EXAMPLES.map((e) => (
              <button
                key={e.romaji}
                onClick={() => setInput(e.romaji)}
                className="px-3 py-1.5 bg-white border border-[#1A2A40]/15 rounded-full text-sm hover:border-[#E94A3F] hover:text-[#E94A3F] transition-colors"
                title={e.meaning}
              >
                {e.romaji}
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        <div className="grid sm:grid-cols-2 gap-4">
          <ResultRow
            label="Hiragana"
            value={hiragana}
            color="#E94A3F"
            isJp
            copied={copied["Hiragana"]}
            onCopy={() => copyWithWatermark("Hiragana", hiragana)}
            onSpeak={() => speakJp(hiragana)}
          />
          <ResultRow
            label="Katakana"
            value={katakana}
            color="#1A2A40"
            isJp
            copied={copied["Katakana"]}
            onCopy={() => copyWithWatermark("Katakana", katakana)}
            onSpeak={() => speakJp(katakana)}
          />
        </div>
        <ResultRow
          label="Romaji"
          value={romaji}
          color="#8CA07A"
          mono={false}
          copied={copied["Romaji"]}
          onCopy={() => copyWithWatermark("Romaji", romaji)}
        />

        {/* Contextual CTA — only when there's output */}
        {hasOutput && (
          <div
            className="bg-[#1A2A40] text-[#FDFBF7] rounded-2xl p-6 sm:p-8 shadow-brut"
            data-testid="kana-cta-contextual"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-[#E94A3F] flex items-center justify-center">
                <GraduationCap className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-black text-xl sm:text-2xl mb-2">
                  Tu apprends le japonais ? On peut t'aider à aller bien plus loin.
                </div>
                <p className="text-sm text-[#FDFBF7]/80 leading-relaxed mb-4">
                  Sur <strong>Japonais Pas à Pas</strong>, on enseigne le japonais aux francophones
                  en 15 min par jour : 200 leçons du N5 au N3, tuteur IA, audio natif. Essai gratuit
                  3 jours, sans carte bancaire.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/register"
                    data-testid="kana-cta-register"
                    className="inline-flex items-center gap-1.5 bg-[#E94A3F] hover:bg-[#d83a2f] text-[#FDFBF7] font-bold py-2.5 px-4 rounded-xl text-sm transition-colors"
                  >
                    Démarrer 3j gratuit <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/kana"
                    data-testid="kana-cta-kana"
                    className="inline-flex items-center gap-1.5 bg-[#FDFBF7]/10 hover:bg-[#FDFBF7]/20 text-[#FDFBF7] font-bold py-2.5 px-4 rounded-xl text-sm transition-colors"
                  >
                    Apprendre les kana
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer of the tool — 3 portes de sortie */}
        <section
          className="pt-8 border-t border-[#1A2A40]/10"
          data-testid="kana-exit-doors"
        >
          <h2 className="font-display font-black text-2xl text-[#1A2A40] mb-5">
            Pour aller plus loin
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Link
              to="/kana"
              className="bg-white rounded-2xl border-2 border-[#1A2A40]/10 p-5 hover-lift block group"
            >
              <BookOpen className="w-6 h-6 text-[#E94A3F] mb-2" />
              <div className="font-bold text-[#1A2A40] mb-1 group-hover:text-[#E94A3F] transition-colors">
                Tu débutes ?
              </div>
              <div className="text-sm text-[#4A5568]">
                Apprends les 46 hiragana + 46 katakana avec audio natif et cartes interactives. Gratuit.
              </div>
            </Link>
            <Link
              to="/vocabulaire"
              className="bg-white rounded-2xl border-2 border-[#1A2A40]/10 p-5 hover-lift block group"
            >
              <Sparkles className="w-6 h-6 text-[#FFD166] mb-2" />
              <div className="font-bold text-[#1A2A40] mb-1 group-hover:text-[#E94A3F] transition-colors">
                Tu connais déjà les kana ?
              </div>
              <div className="text-sm text-[#4A5568]">
                636 mots de vocabulaire JLPT N5 classés par thème, avec audio natif. Gratuit.
              </div>
            </Link>
            <Link
              to="/blog/voyager-japon-50-phrases-utiles"
              className="bg-white rounded-2xl border-2 border-[#1A2A40]/10 p-5 hover-lift block group"
            >
              <GraduationCap className="w-6 h-6 text-[#8CA07A] mb-2" />
              <div className="font-bold text-[#1A2A40] mb-1 group-hover:text-[#E94A3F] transition-colors">
                Tu pars au Japon ?
              </div>
              <div className="text-sm text-[#4A5568]">
                50 phrases japonaises essentielles pour voyager. Article complet + PDF offert.
              </div>
            </Link>
          </div>
        </section>

        {/* Mini FAQ — boost SEO + answers common questions */}
        <section className="pt-10" data-testid="kana-faq">
          <h2 className="font-display font-black text-2xl text-[#1A2A40] mb-5">
            Comment ça marche ?
          </h2>
          <div className="space-y-3 text-[#4A5568]">
            <details className="bg-white rounded-xl border border-[#1A2A40]/10 p-4 group">
              <summary className="cursor-pointer font-bold text-[#1A2A40] flex items-center justify-between">
                <span>Qu'est-ce que le romaji, l'hiragana et le katakana ?</span>
                <span className="text-[#E94A3F] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 leading-relaxed">
                Le <strong>romaji</strong> est la transcription du japonais en alphabet latin
                (ex. <em>konnichiwa</em>). L'<strong>hiragana</strong> (ひらがな) est le syllabaire
                japonais utilisé pour les mots japonais natifs et la grammaire. Le{" "}
                <strong>katakana</strong> (カタカナ) sert pour les mots étrangers, les onomatopées et
                les noms scientifiques. Notre convertisseur génère les trois à partir de n'importe
                lequel.
              </p>
            </details>
            <details className="bg-white rounded-xl border border-[#1A2A40]/10 p-4 group">
              <summary className="cursor-pointer font-bold text-[#1A2A40] flex items-center justify-between">
                <span>Cet outil est-il vraiment gratuit ?</span>
                <span className="text-[#E94A3F] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 leading-relaxed">
                Oui, 100% gratuit, sans inscription, sans publicité. Il est offert par{" "}
                <strong>Japonais Pas à Pas</strong>, une plateforme d'apprentissage du japonais pour
                francophones. Notre objectif : t'aider à démarrer, et si tu veux aller plus loin
                avec nos 200 leçons structurées, l'essai est gratuit 3 jours.
              </p>
            </details>
            <details className="bg-white rounded-xl border border-[#1A2A40]/10 p-4 group">
              <summary className="cursor-pointer font-bold text-[#1A2A40] flex items-center justify-between">
                <span>Quelle romanisation est utilisée ?</span>
                <span className="text-[#E94A3F] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 leading-relaxed">
                La <strong>romanisation Hepburn</strong> (la plus utilisée internationalement) est
                supportée, ainsi que les variantes Kunrei et Nippon-shiki courantes. Les sokuon
                (consonnes doublées, ex. <em>matte</em> → まって) et yoon (palatalisations, ex.{" "}
                <em>kyou</em> → きょう) sont gérés.
              </p>
            </details>
            <details className="bg-white rounded-xl border border-[#1A2A40]/10 p-4 group">
              <summary className="cursor-pointer font-bold text-[#1A2A40] flex items-center justify-between">
                <span>Puis-je partager mon résultat ?</span>
                <span className="text-[#E94A3F] text-2xl group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 leading-relaxed">
                Oui. Clique sur <strong>Partager</strong> pour copier un lien direct vers ta
                conversion (avec ton texte pré-rempli). Idéal pour Discord, WhatsApp, Reddit ou pour
                envoyer à un ami qui apprend aussi le japonais.
              </p>
            </details>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default RomajiKanaTool;
